// SPDX-License-Identifier: AGPL-3.0-or-later

import {getElectronAPI} from '@app/features/ui/utils/NativeUtils';
import type {VoiceDiagnosticLevel, VoiceDiagnosticRecordInput} from '@app/types/electron.d';

const DB_NAME = 'fluxer-voice-diagnostics';
const DB_VERSION = 1;
const STORE_NAME = 'entries';
const MAX_LOG_BYTES = 10 * 1024 * 1024;
const FLUSH_DELAY_MS = 250;
const MAX_FIELD_STRING_LENGTH = 4000;
const MAX_ARRAY_ITEMS = 64;
const MAX_OBJECT_KEYS = 64;
const MAX_SANITIZE_DEPTH = 5;
interface StoredVoiceDiagnosticRecord extends VoiceDiagnosticRecordInput {
	id: string;
	timestamp: string;
	level: VoiceDiagnosticLevel;
	source: 'app-renderer';
	sizeBytes: number;
}
interface VoiceDiagnosticLogger {
	debug: (event: string, data?: Record<string, unknown>) => void;
	info: (event: string, data?: Record<string, unknown>) => void;
	warn: (event: string, data?: Record<string, unknown>) => void;
	error: (event: string, data?: Record<string, unknown>) => void;
}
let pendingRecords: Array<StoredVoiceDiagnosticRecord> = [];
let flushTimer: number | null = null;
let flushPromise: Promise<void> = Promise.resolve();
let indexedDbUnavailable = false;
function byteLength(value: string): number {
	try {
		return new TextEncoder().encode(value).byteLength;
	} catch {
		return value.length;
	}
}
function sanitizeError(error: Error): Record<string, unknown> {
	return {
		name: error.name,
		message: error.message,
		stack: error.stack,
	};
}
function sanitizeValue(value: unknown, depth = 0, seen = new WeakSet<object>()): unknown {
	if (value == null) return value;
	if (value instanceof Error) return sanitizeError(value);
	if (typeof value === 'string') {
		return value.length > MAX_FIELD_STRING_LENGTH ? `${value.slice(0, MAX_FIELD_STRING_LENGTH)}...<truncated>` : value;
	}
	const valueType = typeof value;
	if (valueType === 'number') return Number.isFinite(value) ? value : String(value);
	if (valueType === 'boolean') return value;
	if (valueType === 'bigint') return value.toString();
	if (valueType === 'symbol' || valueType === 'function' || valueType === 'undefined') return String(value);
	if (depth >= MAX_SANITIZE_DEPTH) {
		return '<max-depth>';
	}
	if (Array.isArray(value)) {
		const items = value.slice(0, MAX_ARRAY_ITEMS).map((item) => sanitizeValue(item, depth + 1, seen));
		if (value.length > MAX_ARRAY_ITEMS) {
			items.push(`<${value.length - MAX_ARRAY_ITEMS} more items>`);
		}
		return items;
	}
	if (valueType === 'object') {
		const objectValue = value as Record<string, unknown>;
		if (seen.has(objectValue)) {
			return '<circular>';
		}
		seen.add(objectValue);
		const result: Record<string, unknown> = {};
		const entries = Object.entries(objectValue).slice(0, MAX_OBJECT_KEYS);
		for (const [key, entryValue] of entries) {
			result[key] = sanitizeValue(entryValue, depth + 1, seen);
		}
		const omitted = Object.keys(objectValue).length - entries.length;
		if (omitted > 0) {
			result.__omittedKeys = omitted;
		}
		return result;
	}
	return String(value);
}
function createRecord(
	component: string,
	event: string,
	level: VoiceDiagnosticLevel,
	data?: Record<string, unknown>,
): StoredVoiceDiagnosticRecord {
	const timestamp = new Date().toISOString();
	const baseRecord: VoiceDiagnosticRecordInput = {
		timestamp,
		level,
		source: 'app-renderer',
		component: component.slice(0, 120),
		event: event.slice(0, 160),
		...(data ? {data: sanitizeValue(data) as Record<string, unknown>} : {}),
	};
	const serialized = JSON.stringify(baseRecord);
	return {
		...baseRecord,
		id: `${timestamp}-${Math.random().toString(36).slice(2)}`,
		timestamp,
		level,
		source: 'app-renderer',
		sizeBytes: byteLength(serialized) + 1,
	};
}
function requestToPromise<T>(request: IDBRequest<T>): Promise<T> {
	return new Promise((resolve, reject) => {
		request.onsuccess = () => resolve(request.result);
		request.onerror = () => reject(request.error);
	});
}
function transactionDone(transaction: IDBTransaction): Promise<void> {
	return new Promise((resolve, reject) => {
		transaction.oncomplete = () => resolve();
		transaction.onerror = () => reject(transaction.error);
		transaction.onabort = () => reject(transaction.error);
	});
}
function openDatabase(): Promise<IDBDatabase> {
	return new Promise((resolve, reject) => {
		if (indexedDbUnavailable || typeof indexedDB === 'undefined') {
			reject(new Error('IndexedDB unavailable'));
			return;
		}
		const request = indexedDB.open(DB_NAME, DB_VERSION);
		request.onupgradeneeded = () => {
			const db = request.result;
			if (!db.objectStoreNames.contains(STORE_NAME)) {
				const store = db.createObjectStore(STORE_NAME, {keyPath: 'id'});
				store.createIndex('timestamp', 'timestamp');
			}
		};
		request.onsuccess = () => resolve(request.result);
		request.onerror = () => reject(request.error);
		request.onblocked = () => reject(new Error('IndexedDB open blocked'));
	});
}
async function pruneIndexedDb(db: IDBDatabase): Promise<void> {
	const readTransaction = db.transaction(STORE_NAME, 'readonly');
	const records = await requestToPromise<Array<StoredVoiceDiagnosticRecord>>(
		readTransaction.objectStore(STORE_NAME).getAll(),
	);
	await transactionDone(readTransaction);
	let totalBytes = records.reduce((sum, record) => sum + (record.sizeBytes || 0), 0);
	if (totalBytes <= MAX_LOG_BYTES) return;
	records.sort((a, b) =>
		a.timestamp === b.timestamp ? a.id.localeCompare(b.id) : a.timestamp.localeCompare(b.timestamp),
	);
	const deleteIds: Array<string> = [];
	for (const record of records) {
		if (totalBytes <= MAX_LOG_BYTES) break;
		deleteIds.push(record.id);
		totalBytes -= record.sizeBytes || 0;
	}
	if (deleteIds.length === 0) return;
	const writeTransaction = db.transaction(STORE_NAME, 'readwrite');
	const store = writeTransaction.objectStore(STORE_NAME);
	for (const id of deleteIds) {
		store.delete(id);
	}
	await transactionDone(writeTransaction);
}
async function flushIndexedDb(records: Array<StoredVoiceDiagnosticRecord>): Promise<void> {
	if (records.length === 0) return;
	const db = await openDatabase();
	try {
		const transaction = db.transaction(STORE_NAME, 'readwrite');
		const store = transaction.objectStore(STORE_NAME);
		for (const record of records) {
			store.put(record);
		}
		await transactionDone(transaction);
		await pruneIndexedDb(db);
	} finally {
		db.close();
	}
}
function scheduleIndexedDbFlush(): void {
	if (flushTimer != null || indexedDbUnavailable) return;
	flushTimer = window.setTimeout(() => {
		flushTimer = null;
		const records = pendingRecords;
		pendingRecords = [];
		flushPromise = flushPromise
			.then(() => flushIndexedDb(records))
			.catch(() => {
				indexedDbUnavailable = true;
			});
	}, FLUSH_DELAY_MS);
}
function enqueueIndexedDb(record: StoredVoiceDiagnosticRecord): void {
	if (indexedDbUnavailable) return;
	pendingRecords.push(record);
	scheduleIndexedDbFlush();
}
async function flushPendingIndexedDbRecords(): Promise<void> {
	if (indexedDbUnavailable) return;
	if (flushTimer != null) {
		window.clearTimeout(flushTimer);
		flushTimer = null;
	}
	if (pendingRecords.length > 0) {
		const records = pendingRecords;
		pendingRecords = [];
		flushPromise = flushPromise
			.then(() => flushIndexedDb(records))
			.catch(() => {
				indexedDbUnavailable = true;
			});
	}
	await flushPromise;
}
export function recordVoiceDiagnostic(
	component: string,
	event: string,
	data?: Record<string, unknown>,
	level: VoiceDiagnosticLevel = 'info',
): void {
	const record = createRecord(component, event, level, data);
	const electronApi = getElectronAPI();
	if (electronApi?.voiceDiagnostics) {
		void electronApi.voiceDiagnostics.record(record).catch(() => {
			enqueueIndexedDb(record);
		});
		return;
	}
	enqueueIndexedDb(record);
}
export function createVoiceDiagnosticLogger(component: string): VoiceDiagnosticLogger {
	return {
		debug: (event, data) => recordVoiceDiagnostic(component, event, data, 'debug'),
		info: (event, data) => recordVoiceDiagnostic(component, event, data, 'info'),
		warn: (event, data) => recordVoiceDiagnostic(component, event, data, 'warn'),
		error: (event, data) => recordVoiceDiagnostic(component, event, data, 'error'),
	};
}
export async function readVoiceDiagnosticsLog(): Promise<string> {
	const electronApi = getElectronAPI();
	if (electronApi?.voiceDiagnostics) {
		return electronApi.voiceDiagnostics.read();
	}
	await flushPendingIndexedDbRecords();
	const db = await openDatabase();
	try {
		const transaction = db.transaction(STORE_NAME, 'readonly');
		const records = await requestToPromise<Array<StoredVoiceDiagnosticRecord>>(
			transaction.objectStore(STORE_NAME).getAll(),
		);
		await transactionDone(transaction);
		records.sort((a, b) =>
			a.timestamp === b.timestamp ? a.id.localeCompare(b.id) : a.timestamp.localeCompare(b.timestamp),
		);
		return records.map(({id: _id, sizeBytes: _sizeBytes, ...record}) => JSON.stringify(record)).join('\n');
	} finally {
		db.close();
	}
}
export async function clearVoiceDiagnosticsLog(): Promise<void> {
	const electronApi = getElectronAPI();
	if (electronApi?.voiceDiagnostics) {
		await electronApi.voiceDiagnostics.clear();
		return;
	}
	await flushPendingIndexedDbRecords();
	const db = await openDatabase();
	try {
		const transaction = db.transaction(STORE_NAME, 'readwrite');
		transaction.objectStore(STORE_NAME).clear();
		await transactionDone(transaction);
	} finally {
		db.close();
	}
}
